/**
 * Client-side attachment rules. The server enforces the same limits; these
 * exist so the picker refuses before a request is made, and the 422 is only
 * ever a backstop.
 */

export const MAX_FILES_PER_MESSAGE = 10;
export const MAX_FILE_BYTES = 1024 ** 3; // 1 GB
/** Mirrors PREVIEW_MAX_BYTES in backend/app/services/chat/attachments.py. */
export const PREVIEW_MAX_BYTES = 100 * 1024 ** 2;
export const FOLDER_NOTICE =
  'Folders are not supported. Compress the folder to a ZIP file first.';

const BLOCKED_EXTENSIONS: ReadonlySet<string> = new Set([
  'exe',
  'msi',
  'dll',
  'com',
  'scr',
  'bat',
  'cmd',
  'ps1',
  'sh',
  'vbs',
  'js',
  'jar',
  'apk',
  'dmg',
  'app',
  'pkg',
  // N-03 owner ruling.
  'lnk',
  'hta',
  'reg',
  'vbe',
  'wsf',
  'cpl',
  'iso',
  'msix',
  'appx',
  'command',
  'url',
]);

export type FileFamily =
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'presentation'
  | 'archive'
  | 'image'
  | 'video'
  | 'audio'
  | 'code'
  | 'other';

const FAMILY_BY_EXTENSION: Record<string, FileFamily> = {
  pdf: 'pdf',
  doc: 'document',
  docx: 'document',
  txt: 'document',
  rtf: 'document',
  xls: 'spreadsheet',
  xlsx: 'spreadsheet',
  csv: 'spreadsheet',
  ppt: 'presentation',
  pptx: 'presentation',
  zip: 'archive',
  gz: 'archive',
  tar: 'archive',
  rar: 'archive',
  '7z': 'archive',
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  heic: 'image',
  mp4: 'video',
  mov: 'video',
  avi: 'video',
  webm: 'video',
  mp3: 'audio',
  m4a: 'audio',
  wav: 'audio',
  aac: 'audio',
  py: 'code',
  ts: 'code',
  tsx: 'code',
  json: 'code',
  xml: 'code',
  sql: 'code',
};

// Windows drops trailing dots and spaces when it saves a file, and a
// zero-width character is invisible, so none of them may hide the extension.
// Mirrors _IGNORED_TRAILING in backend/app/services/chat/attachments.py.
const IGNORED_TRAILING = /[. \u200B-\u200D\u2060\uFEFF]+$/;

export const extensionOf = (fileName: string): string => {
  const name = fileName.replace(IGNORED_TRAILING, '');
  const index = name.lastIndexOf('.');
  return index === -1 ? '' : name.slice(index + 1).toLowerCase();
};

export const fileFamily = (fileName: string): FileFamily =>
  FAMILY_BY_EXTENSION[extensionOf(fileName)] ?? 'other';

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 ** 3) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
};

/** The notice text, or null when the file may be attached. */
export const rejectReason = (
  file: File,
  existingCount: number
): string | null => {
  if (existingCount >= MAX_FILES_PER_MESSAGE) {
    return `You can attach up to ${MAX_FILES_PER_MESSAGE} files to a message.`;
  }
  if (BLOCKED_EXTENSIONS.has(extensionOf(file.name))) {
    return 'That file type cannot be sent in chat.';
  }
  if (file.size > MAX_FILE_BYTES) return 'Files must be 1 GB or smaller.';
  return null;
};

/**
 * Filter one batch of files, reporting the reason for each refusal. Every
 * intake route — picker, camera, drop, paste — runs this, so one notice text
 * covers all four.
 */
export const acceptFiles = (
  files: File[],
  existingCount: number,
  onNotice: (message: string) => void
): File[] => {
  const accepted: File[] = [];
  for (const file of files) {
    const reason = rejectReason(file, existingCount + accepted.length);
    if (reason) {
      onNotice(reason);
      continue;
    }
    accepted.push(file);
  }
  return accepted;
};
